/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Payment methods manager for the signed-in renter. Cards are kept PER RENTAL COMPANY (each driver/owner pair
 * has its own card set), so the cards are shown grouped by company (GET tollPayment/my-card-owners); within a
 * company the renter can pick the active (default) charge card, delete a non-active card, or add another.
 * A driver can keep several cards with a company at once (e.g. a US and an Indian card); that company's
 * charges use the active card and automatically fall back to the others on decline.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, CheckCircle2, Trash2, Plus, Loader2, Star, Building2 } from 'lucide-react';
import api from '../../services/api';
import type { CardOwner, SavedCard } from '../../types';
import CardModal from './CardModal';

/** A card's payment-method id, needed for set-active / delete. */
const idOf = (c: SavedCard) => c.stripePaymentMethodId ?? '';

export default function PaymentMethods() {
  const { t } = useTranslation();
  const userId = api.getStoredUser()?.id ?? '';
  // null = loading. A failed lookup (e.g. an older backend without my-card-owners) degrades to one unnamed
  // section without an owner — the server then picks the storefront's owner / the driver's only company.
  const [owners, setOwners] = useState<CardOwner[] | null>(null);

  useEffect(() => {
    if (!userId) {
      setOwners([]);
      return;
    }
    let live = true;
    api
      .getMyCardOwners()
      .then((list) => live && setOwners(list))
      .catch(() => live && setOwners([{ ownerId: '', name: '' }]));
    return () => {
      live = false;
    };
  }, [userId]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-4">
        <CreditCard size={18} className="opacity-70" />
        {t('cards.title', 'Payment methods')}
      </h2>

      {owners === null ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('detail.loading', 'Loading…')}
        </div>
      ) : owners.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">
          {t('cards.noCompanies', 'No rental companies yet. You add a card when you book a car.')}
        </p>
      ) : (
        <div className="space-y-5">
          {owners.length > 1 && (
            <p className="text-xs text-gray-400 -mt-2">
              {t('cards.perCompanyHint', 'Cards are kept separately for each rental company.')}
            </p>
          )}
          {owners.map((o) => (
            <CompanyCards key={o.ownerId || 'default'} owner={o} userId={userId} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One rental company's card set with its own add / switch / delete actions. */
function CompanyCards({ owner, userId }: { owner: CardOwner; userId: string }) {
  const { t } = useTranslation();
  const scope = owner.ownerId ? { ownerId: owner.ownerId } : undefined;
  const [cards, setCards] = useState<SavedCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setCards([]);
      return;
    }
    setError(null);
    try {
      const list = await api.getCards(userId, owner.ownerId ? { ownerId: owner.ownerId } : undefined);
      setCards(list);
    } catch {
      setCards([]);
      setError(t('cards.loadFailed', 'Could not load your cards.'));
    }
  }, [userId, owner.ownerId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const makeActive = async (c: SavedCard) => {
    const pm = idOf(c);
    if (!pm) return;
    setBusyId(pm);
    setError(null);
    try {
      const res = await api.setActiveCard(userId, pm, scope);
      if (!res.ok) {
        setError(res.message || t('cards.setActiveFailed', 'Could not switch the active card.'));
        return;
      }
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (c: SavedCard) => {
    const pm = idOf(c);
    if (!pm) return;
    if (!window.confirm(t('cards.confirmDelete', 'Remove this card?'))) return;
    setBusyId(pm);
    setError(null);
    try {
      const res = await api.deleteCard(userId, pm, scope);
      if (!res.ok) {
        // The backend refuses to delete the active card — surface its message.
        setError(res.message || t('cards.deleteFailed', 'Could not remove the card.'));
        return;
      }
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const fmtExp = (c: SavedCard) =>
    c.expirationMonth && c.expirationYear
      ? `${String(c.expirationMonth).padStart(2, '0')}/${String(c.expirationYear).slice(-2)}`
      : '';

  return (
    <section>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 min-w-0">
          {owner.name && (
            <>
              <Building2 size={16} className="shrink-0 opacity-60" />
              <span className="truncate">{owner.name}</span>
            </>
          )}
        </h3>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 shrink-0"
        >
          <Plus size={15} /> {t('cards.add', 'Add card')}
        </button>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      {cards === null ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('detail.loading', 'Loading…')}
        </div>
      ) : cards.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">
          {owner.ownerId
            ? t('cards.emptyCompany', 'No cards on file with this company yet.')
            : t('cards.empty', 'No cards on file yet. Add one to book and to cover tolls.')}
        </p>
      ) : (
        <ul className="space-y-2">
          {cards.map((c) => {
            const pm = idOf(c);
            const busy = busyId === pm;
            const active = !!c.isActive;
            return (
              <li
                key={pm || `${c.brand}-${c.last4}`}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${
                  active ? 'border-green-300 bg-green-50/60' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <CreditCard size={18} className="shrink-0 text-gray-500" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      <span className="capitalize">{c.brand || t('cards.card', 'Card')}</span> •• {c.last4 || '····'}
                    </div>
                    {fmtExp(c) && (
                      <div className="text-xs text-gray-500">
                        {t('cards.expires', 'Expires')} {fmtExp(c)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {active ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                      <CheckCircle2 size={15} /> {t('cards.active', 'Active')}
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => makeActive(c)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star size={14} />}
                        {t('cards.makeActive', 'Make active')}
                      </button>
                      <button
                        onClick={() => remove(c)}
                        disabled={busy}
                        aria-label={t('cards.delete', 'Remove card')}
                        title={t('cards.delete', 'Remove card')}
                        className="inline-flex items-center justify-center p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {cards && cards.length > 1 && (
        <p className="mt-3 text-xs text-gray-400">
          {t(
            'cards.fallbackHint',
            'Charges use your active card and automatically try your other cards if it is declined.',
          )}
        </p>
      )}

      {adding && (
        <CardModal
          ownerId={owner.ownerId || undefined}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            load();
          }}
        />
      )}
    </section>
  );
}
